import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Button, Form, Table } from "react-bootstrap";

import { translate } from "@/modules/shared/IntlProviderWrapper";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import type { IGetLatestModelEvaluationKpiAggregationsRes } from "@/lib/backend/reports.generated.types";
import type { ILineData } from "@/lib/backend/manufacturing2.generated.types";
import { getLineGroupsForUser, getPlantsForUser, waitForManufacturingAsync } from "@/lib/businessLogic";
import {
    buildEntityLabelMaps,
    DEFAULT_MODEL_EVALUATION_SCOPE,
    defaultSortForScope,
    formatCountWithPct,
    formatDurationHours,
    formatErrorRatioPct,
    getScopeAggregate,
    getSubordinateRows,
    normalizeScope,
    scopeFromDrillRow,
    sortSubordinateRows,
    type ModelEvaluationScope,
    type ModelEvaluationScopeType,
    type ModelEvaluationWindowKey,
    type SubordinateRow,
    type SubordinateSort,
    type SubordinateSortKey,
} from "@/lib/ModelEvaluationKpis";

import WinMixBar from "./WinMixBar";
import ModelEvaluationFilterBar from "./ModelEvaluationFilterBar";
import { SortableKpiTh, WinMixColumnHeader, sortDirectionIndicator } from "./modelEvaluationTable";
import "./AggregatedReportsTab.scss";

type Props = {
    aggregations: IGetLatestModelEvaluationKpiAggregationsRes;
    window: ModelEvaluationWindowKey;
    onOpenModelsTab: (handoff: { plant_id: number; line_id: number }) => void;
};

const SCOPE_TYPES: ModelEvaluationScopeType[] = ["organization", "plant", "line_group", "line"];

const scopeTypeTranslationId = (scope_type: ModelEvaluationScopeType) =>
    `Manufacturing.ModelEvaluation.scope.type.${scope_type}`;

function KpiCards(props: { aggregate: NonNullable<ReturnType<typeof getScopeAggregate>> }) {
    const { aggregate } = props;
    const ops = aggregate.operations_count || 0;
    const fallback_count = aggregate.fallback_count ?? 0;

    return (
        <div
            className="d-flex flex-responsive mb-3 gap-3"
            style={{ "--min-child-width": "12rem" } as CSSProperties}
            role="group"
            aria-label="KPIs for selected scope"
        >
            <div className="card gap-3">
                <div className="card-title">
                    {translate("Manufacturing.ModelEvaluation.kpi.operations", "Operations")}
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">
                        {translate("Manufacturing.ModelEvaluation.kpi.operations_count", "Operations count")}
                    </span>
                    <span className="card-value">{ops.toLocaleString()}</span>
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">
                        {translate("Manufacturing.ModelEvaluation.kpi.total_duration", "Total duration (h)")}
                    </span>
                    <span className="card-value">{formatDurationHours(aggregate.total_duration_hours)}</span>
                </div>
            </div>
            <div className="card gap-3">
                <div className="card-title">{translate("Manufacturing.ModelEvaluation.kpi.win_mix", "Win mix")}</div>
                <div className="card-row align-items-start">
                    <span className="card-label">
                        {translate("Manufacturing.ModelEvaluation.kpi.model_wins", "Model wins")}
                    </span>
                    <span className="card-value">{formatCountWithPct(aggregate.model_win_count, ops)}</span>
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">
                        {translate("Manufacturing.ModelEvaluation.kpi.normative_wins", "Normative wins")}
                    </span>
                    <span className="card-value">{formatCountWithPct(aggregate.normative_win_count, ops)}</span>
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">
                        {translate("Manufacturing.ModelEvaluation.kpi.fallback", "Fallback")}
                    </span>
                    <span className="card-value">{formatCountWithPct(fallback_count, ops)}</span>
                </div>
                <div className="win-mix-card-bar pt-1">
                    <WinMixBar
                        model_wins={aggregate.model_win_count}
                        norm_wins={aggregate.normative_win_count}
                        fallback_count={fallback_count}
                    />
                </div>
            </div>
            <div className="card gap-3">
                <div className="card-title">
                    {translate("Manufacturing.ModelEvaluation.kpi.average_errors", "Average errors")}
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">
                        {translate("Manufacturing.ModelEvaluation.kpi.avg_model_error", "Average model error")}
                    </span>
                    <span className="card-value">{formatErrorRatioPct(aggregate.avg_model_error_ratio_pct)}</span>
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">
                        {translate("Manufacturing.ModelEvaluation.kpi.avg_normative_error", "Average normative error")}
                    </span>
                    <span className="card-value">{formatErrorRatioPct(aggregate.avg_normative_error_ratio_pct)}</span>
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">
                        {translate(
                            "Manufacturing.ModelEvaluation.kpi.avg_model_norm_ratio",
                            "Average model / normative error ratio"
                        )}
                    </span>
                    <span className="card-value">
                        {formatErrorRatioPct(aggregate.avg_model_error_against_norm_ratio_pct)}
                    </span>
                </div>
            </div>
        </div>
    );
}

function SubordinateTable(props: {
    scope_type: ModelEvaluationScopeType;
    rows: SubordinateRow[];
    sort: SubordinateSort;
    onSortChange: (sort: SubordinateSort) => void;
    onRowClick: (row: SubordinateRow) => void;
}) {
    const column_label =
        props.scope_type === "organization"
            ? translate("Manufacturing.ModelEvaluation.table.column.plant", "Plant")
            : props.scope_type === "plant"
              ? translate("Manufacturing.ModelEvaluation.table.column.line_group", "Line group")
              : translate("Manufacturing.ModelEvaluation.table.column.line", "Line");

    const handleSort = (key: SubordinateSortKey) => {
        if (props.sort.key === key) {
            props.onSortChange({ key, dir: (props.sort.dir * -1) as 1 | -1 });
        } else {
            props.onSortChange({ key, dir: key === "label" ? 1 : -1 });
        }
    };

    const sortableTh = (key: SubordinateSortKey, label: string, numeric = false) => (
        <SortableKpiTh
            sort_key={key}
            label={label}
            numeric={numeric}
            onClick={() => {
                handleSort(key);
            }}
            indicator={sortDirectionIndicator(props.sort.key, key, props.sort.dir)}
        />
    );

    return (
        <div className="table-responsive model-evaluation-table table-fixed-plant">
            <Table size="sm" striped hover className="dashboard-table mb-0">
                <thead className="table-header-neutral">
                    <tr>
                        {sortableTh("label", column_label)}
                        {sortableTh(
                            "operations_count",
                            translate("Manufacturing.ModelEvaluation.table.column.operations", "Operations"),
                            true
                        )}
                        {sortableTh(
                            "total_duration_hours",
                            translate("Manufacturing.ModelEvaluation.kpi.total_duration", "Total duration (h)"),
                            true
                        )}
                        {sortableTh(
                            "model_win_count",
                            translate("Manufacturing.ModelEvaluation.kpi.model_wins", "Model wins"),
                            true
                        )}
                        {sortableTh(
                            "normative_win_count",
                            translate("Manufacturing.ModelEvaluation.kpi.normative_wins", "Norm wins"),
                            true
                        )}
                        {sortableTh(
                            "fallback_count",
                            translate("Manufacturing.ModelEvaluation.kpi.fallback", "Fallback"),
                            true
                        )}
                        <WinMixColumnHeader />
                        {sortableTh(
                            "avg_model_error_ratio_pct",
                            translate("Manufacturing.ModelEvaluation.table.column.avg_model_err", "Avg model err"),
                            true
                        )}
                        {sortableTh(
                            "avg_normative_error_ratio_pct",
                            translate("Manufacturing.ModelEvaluation.table.column.avg_norm_err", "Avg norm err"),
                            true
                        )}
                    </tr>
                </thead>
                <tbody>
                    {props.rows.map(row => {
                        const aggregate = row.aggregate;
                        const ops = aggregate.operations_count || 0;
                        return (
                            <tr
                                key={`${row.drill}-${row.id}`}
                                className="scope-row-clickable"
                                onClick={() => {
                                    props.onRowClick(row);
                                }}
                                title={translate(
                                    "Manufacturing.ModelEvaluation.table.drill_hint",
                                    "Drill to narrower scope"
                                )}
                            >
                                <td>{row.label}</td>
                                <td className="text-right">{ops.toLocaleString()}</td>
                                <td className="text-right">{formatDurationHours(aggregate.total_duration_hours)}</td>
                                <td className="text-right">{formatCountWithPct(aggregate.model_win_count, ops)}</td>
                                <td className="text-right">{formatCountWithPct(aggregate.normative_win_count, ops)}</td>
                                <td className="text-right">{formatCountWithPct(aggregate.fallback_count ?? 0, ops)}</td>
                                <td className="text-center align-middle py-2">
                                    <WinMixBar
                                        model_wins={aggregate.model_win_count}
                                        norm_wins={aggregate.normative_win_count}
                                        fallback_count={aggregate.fallback_count ?? 0}
                                    />
                                </td>
                                <td className="text-right">
                                    {formatErrorRatioPct(aggregate.avg_model_error_ratio_pct)}
                                </td>
                                <td className="text-right">
                                    {formatErrorRatioPct(aggregate.avg_normative_error_ratio_pct)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}

export default function AggregatedReportsTab(props: Props) {
    const [scope, setScope] = useState<ModelEvaluationScope>(DEFAULT_MODEL_EVALUATION_SCOPE);
    const [sort, setSort] = useState<SubordinateSort>(defaultSortForScope("organization"));
    const [lines, setLines] = useState<ILineData[]>([]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            await waitForManufacturingAsync();
            if (cancelled) {
                return;
            }
            try {
                const res = await getBackend2().manufacturing.getLines({});
                if (!cancelled) {
                    setLines(res.lines);
                }
            } catch (err: unknown) {
                console.error((err as Error).message);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    const label_maps = useMemo(
        () => buildEntityLabelMaps(props.aggregations, getPlantsForUser(), getLineGroupsForUser(), lines),
        [props.aggregations, lines]
    );

    const normalized_scope = useMemo(() => normalizeScope(props.aggregations, scope), [props.aggregations, scope]);

    useEffect(() => {
        if (
            normalized_scope.type !== scope.type ||
            normalized_scope.plant_id !== scope.plant_id ||
            normalized_scope.line_group_id !== scope.line_group_id ||
            normalized_scope.line_id !== scope.line_id
        ) {
            setScope(normalized_scope);
        }
    }, [normalized_scope, scope]);

    const scope_aggregate = getScopeAggregate(props.aggregations, normalized_scope, props.window);
    const subordinate_rows = sortSubordinateRows(
        getSubordinateRows(props.aggregations, normalized_scope, props.window, label_maps),
        sort
    );

    const organization_title =
        props.aggregations.metadata.org_title ??
        translate("Manufacturing.ModelEvaluation.scope.organization_default", "Organization");

    const plants_in_report = props.aggregations.plants;
    const selected_plant =
        normalized_scope.plant_id != null
            ? plants_in_report.find(plant => plant.id === normalized_scope.plant_id)
            : null;
    const line_groups_for_plant = selected_plant?.line_groups ?? [];

    const handleScopeTypeChange = (scope_type: ModelEvaluationScopeType) => {
        setScope(
            normalizeScope(props.aggregations, {
                ...DEFAULT_MODEL_EVALUATION_SCOPE,
                type: scope_type,
                plant_id: normalized_scope.plant_id,
                line_group_id: normalized_scope.line_group_id,
                line_id: normalized_scope.line_id,
            })
        );
        setSort(defaultSortForScope(scope_type));
    };

    const handleClearFilter = () => {
        setScope(DEFAULT_MODEL_EVALUATION_SCOPE);
        setSort(defaultSortForScope("organization"));
    };

    const handleDrillRow = useCallback((row: SubordinateRow) => {
        const next_scope = scopeFromDrillRow(row);
        setScope(next_scope);
        setSort(defaultSortForScope(next_scope.type));
    }, []);

    const subordinate_title =
        normalized_scope.type === "organization"
            ? translate("Manufacturing.ModelEvaluation.table.title.plants", "Plants")
            : normalized_scope.type === "plant"
              ? translate("Manufacturing.ModelEvaluation.table.title.line_groups", "Line groups")
              : translate("Manufacturing.ModelEvaluation.table.title.lines", "Lines");

    const scope_hint =
        normalized_scope.type === "organization"
            ? translate(
                  "Manufacturing.ModelEvaluation.scope.hint.organization",
                  "Viewing organization-level KPIs. Click a plant row to drill down."
              )
            : normalized_scope.type === "plant"
              ? translate(
                    "Manufacturing.ModelEvaluation.scope.hint.plant",
                    "Viewing plant-level KPIs. Click a line group row to drill down."
                )
              : normalized_scope.type === "line_group"
                ? translate(
                      "Manufacturing.ModelEvaluation.scope.hint.line_group",
                      "Viewing line group-level KPIs. Click a line row to drill down."
                  )
                : translate(
                      "Manufacturing.ModelEvaluation.scope.hint.line",
                      "Viewing line-level KPIs. Open the Models tab for per-model detail."
                  );

    const can_clear = normalized_scope.type !== "organization";

    return (
        <div className="aggregated-reports-tab">
            <ModelEvaluationFilterBar footer={scope_hint}>
                <div className="form-row align-items-end">
                    <div className="form-group col-lg-3 col-md-6 mb-md-0">
                        <Form.Label className="mb-1" htmlFor="model-eval-scope-type">
                            {translate("Manufacturing.ModelEvaluation.scope.selected", "Selected scope")}
                        </Form.Label>
                        <Form.Control
                            id="model-eval-scope-type"
                            as="select"
                            size="sm"
                            value={normalized_scope.type}
                            onChange={event => {
                                handleScopeTypeChange(event.target.value as ModelEvaluationScopeType);
                            }}
                            aria-label={translate(
                                "Manufacturing.ModelEvaluation.scope.type_label",
                                "Aggregation scope type"
                            )}
                        >
                            {SCOPE_TYPES.map(scope_type => (
                                <option key={scope_type} value={scope_type}>
                                    {translate(scopeTypeTranslationId(scope_type), scope_type)}
                                </option>
                            ))}
                        </Form.Control>
                    </div>
                    <div className="form-group col-lg-6 col-md-6 mb-0">
                        {normalized_scope.type === "organization" && (
                            <div>
                                <Form.Label className="mb-1">
                                    {translate("Manufacturing.ModelEvaluation.scope.item", "Item")}
                                </Form.Label>
                                <p className="mb-0 small">
                                    <strong>{organization_title}</strong>{" "}
                                    <span className="text-muted">
                                        ({translate(scopeTypeTranslationId("organization"), "Organization")})
                                    </span>
                                </p>
                            </div>
                        )}
                        {normalized_scope.type === "plant" && (
                            <div>
                                <Form.Label className="mb-1" htmlFor="model-eval-scope-plant">
                                    {translate("Manufacturing.ModelEvaluation.table.column.plant", "Plant")}
                                </Form.Label>
                                <Form.Control
                                    id="model-eval-scope-plant"
                                    as="select"
                                    size="sm"
                                    value={normalized_scope.plant_id ?? ""}
                                    onChange={event => {
                                        setScope(
                                            normalizeScope(props.aggregations, {
                                                type: "plant",
                                                plant_id: Number(event.target.value),
                                                line_group_id: null,
                                                line_id: null,
                                            })
                                        );
                                    }}
                                >
                                    {plants_in_report.map(plant => (
                                        <option key={plant.id} value={plant.id}>
                                            {label_maps.plantsById.get(plant.id) ?? `Plant ${plant.id}`}
                                        </option>
                                    ))}
                                </Form.Control>
                            </div>
                        )}
                        {normalized_scope.type === "line_group" && (
                            <div className="form-row">
                                <div className="form-group col-md-6 mb-2 mb-md-0">
                                    <Form.Label className="mb-1" htmlFor="model-eval-scope-plant-lg">
                                        {translate("Manufacturing.ModelEvaluation.table.column.plant", "Plant")}
                                    </Form.Label>
                                    <Form.Control
                                        id="model-eval-scope-plant-lg"
                                        as="select"
                                        size="sm"
                                        value={normalized_scope.plant_id ?? ""}
                                        onChange={event => {
                                            setScope(
                                                normalizeScope(props.aggregations, {
                                                    type: "line_group",
                                                    plant_id: Number(event.target.value),
                                                    line_group_id: null,
                                                    line_id: null,
                                                })
                                            );
                                        }}
                                    >
                                        {plants_in_report.map(plant => (
                                            <option key={plant.id} value={plant.id}>
                                                {label_maps.plantsById.get(plant.id) ?? `Plant ${plant.id}`}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </div>
                                <div className="form-group col-md-6 mb-0">
                                    <Form.Label className="mb-1" htmlFor="model-eval-scope-line-group">
                                        {translate(
                                            "Manufacturing.ModelEvaluation.table.column.line_group",
                                            "Line group"
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        id="model-eval-scope-line-group"
                                        as="select"
                                        size="sm"
                                        value={normalized_scope.line_group_id ?? ""}
                                        onChange={event => {
                                            setScope(
                                                normalizeScope(props.aggregations, {
                                                    type: "line_group",
                                                    plant_id: normalized_scope.plant_id,
                                                    line_group_id: Number(event.target.value),
                                                    line_id: null,
                                                })
                                            );
                                        }}
                                    >
                                        {line_groups_for_plant.map(line_group => (
                                            <option key={line_group.id} value={line_group.id}>
                                                {label_maps.lineGroupsById.get(line_group.id) ??
                                                    `Line group ${line_group.id}`}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </div>
                            </div>
                        )}
                        {normalized_scope.type === "line" && (
                            <div className="form-row">
                                <div className="form-group col-md-6 mb-2 mb-md-0">
                                    <Form.Label className="mb-1" htmlFor="model-eval-scope-plant-line">
                                        {translate("Manufacturing.ModelEvaluation.table.column.plant", "Plant")}
                                    </Form.Label>
                                    <Form.Control
                                        id="model-eval-scope-plant-line"
                                        as="select"
                                        size="sm"
                                        value={normalized_scope.plant_id ?? ""}
                                        onChange={event => {
                                            setScope(
                                                normalizeScope(props.aggregations, {
                                                    type: "line",
                                                    plant_id: Number(event.target.value),
                                                    line_group_id: null,
                                                    line_id: null,
                                                })
                                            );
                                        }}
                                    >
                                        {plants_in_report.map(plant => (
                                            <option key={plant.id} value={plant.id}>
                                                {label_maps.plantsById.get(plant.id) ?? `Plant ${plant.id}`}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </div>
                                <div className="form-group col-md-6 mb-0">
                                    <Form.Label className="mb-1" htmlFor="model-eval-scope-line">
                                        {translate("Manufacturing.ModelEvaluation.table.column.line", "Line")}
                                    </Form.Label>
                                    <Form.Control
                                        id="model-eval-scope-line"
                                        as="select"
                                        size="sm"
                                        value={normalized_scope.line_id ?? ""}
                                        onChange={event => {
                                            const line_id = Number(event.target.value);
                                            let resolved_line_group_id: number | null = null;
                                            for (const line_group of line_groups_for_plant) {
                                                if (line_group.lines.some(line => line.id === line_id)) {
                                                    resolved_line_group_id = line_group.id;
                                                    break;
                                                }
                                            }
                                            setScope(
                                                normalizeScope(props.aggregations, {
                                                    type: "line",
                                                    plant_id: normalized_scope.plant_id,
                                                    line_group_id: resolved_line_group_id,
                                                    line_id,
                                                })
                                            );
                                        }}
                                    >
                                        {line_groups_for_plant.flatMap(line_group =>
                                            line_group.lines.map(line => (
                                                <option key={line.id} value={line.id}>
                                                    {label_maps.linesById.get(line.id) ?? `Line ${line.id}`}
                                                </option>
                                            ))
                                        )}
                                    </Form.Control>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="form-group col-lg-3 col-md-12 mb-0 d-flex justify-content-lg-end justify-content-end align-items-end pt-2 pt-lg-0">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={!can_clear}
                            onClick={handleClearFilter}
                            aria-label={translate(
                                "Manufacturing.ModelEvaluation.scope.clear_filter",
                                "Clear filter and show whole organization"
                            )}
                        >
                            {translate("Manufacturing.ModelEvaluation.scope.clear_filter", "Clear filter")}
                        </Button>
                    </div>
                </div>
            </ModelEvaluationFilterBar>

            {scope_aggregate != null ? (
                <KpiCards aggregate={scope_aggregate} />
            ) : (
                <p className="text-muted">{translate("common.no_data")}</p>
            )}

            {normalized_scope.type !== "line" ? (
                <div className="card p-0 mb-3">
                    <div className="card-title card-title-border-bottom p-3 d-flex justify-content-between align-items-center flex-wrap">
                        <span>{subordinate_title}</span>
                        <span className="badge badge-light text-muted border small font-weight-normal">
                            {translate(
                                "Manufacturing.ModelEvaluation.table.sortable_hint",
                                "Sortable · row = drill down"
                            )}
                        </span>
                    </div>
                    {subordinate_rows.length > 0 ? (
                        <SubordinateTable
                            scope_type={normalized_scope.type}
                            rows={subordinate_rows}
                            sort={sort}
                            onSortChange={setSort}
                            onRowClick={handleDrillRow}
                        />
                    ) : (
                        <p className="text-muted px-3 py-3 mb-0">{translate("common.no_data")}</p>
                    )}
                </div>
            ) : (
                <div className="card p-0 mb-3">
                    <div className="card-content px-3 py-4">
                        <p className="text-muted small mb-3">
                            {translate(
                                "Manufacturing.ModelEvaluation.line_scope.models_hint",
                                "Per-model rows, filters, sorting, and export are on the Models tab. Open it with the current line applied as the line filter (and plant, if known)."
                            )}
                        </p>
                        {scope_aggregate != null && (
                            <p className="small mb-3" aria-live="polite">
                                {translate("Manufacturing.ModelEvaluation.kpi.fallback", "Fallback")}:{" "}
                                {formatCountWithPct(
                                    scope_aggregate.fallback_count ?? 0,
                                    scope_aggregate.operations_count || 0
                                )}
                            </p>
                        )}
                        {normalized_scope.plant_id != null && normalized_scope.line_id != null && (
                            <Button
                                variant="link"
                                className="p-0 font-weight-bold"
                                onClick={() => {
                                    props.onOpenModelsTab({
                                        plant_id: normalized_scope.plant_id as number,
                                        line_id: normalized_scope.line_id as number,
                                    });
                                }}
                            >
                                {translate(
                                    "Manufacturing.ModelEvaluation.line_scope.open_models",
                                    "Open Models tab for this line"
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
